import React, { useState, useEffect, useRef } from 'react';
import { Search, ChevronDown, X, TrendingUp } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { CURRENCIES, REGIONS, getCurrenciesByRegion, POPULAR_CORRIDORS } from '@/utils/currencies';

const CurrencySelector = ({ 
  value, 
  onChange, 
  label = 'Select Currency',
  type = 'from', // 'from' or 'to'
  excludeCurrency = null,
  showPopularCorridors = false,
  otherCurrency = null
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRegion, setSelectedRegion] = useState('all');
  const dropdownRef = useRef(null);

  const selectedCurrency = CURRENCIES.find(c => c.code === value);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filter currencies based on search and region
  const filteredCurrencies = CURRENCIES.filter(currency => {
    if (excludeCurrency && currency.code === excludeCurrency) return false;
    
    const matchesSearch = searchQuery === '' || 
      currency.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      currency.code.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesRegion = selectedRegion === 'all' || currency.region === selectedRegion;
    
    return matchesSearch && matchesRegion;
  });

  // Get relevant popular corridors
  const relevantCorridors = showPopularCorridors && otherCurrency
    ? POPULAR_CORRIDORS.filter(corridor => 
        type === 'from' 
          ? corridor.to === otherCurrency 
          : corridor.from === otherCurrency
      ).slice(0, 3)
    : [];

  const handleSelect = (currency) => {
    onChange(currency.code);
    setIsOpen(false);
    setSearchQuery('');
  };

  return (
    <div className='relative' ref={dropdownRef}>
      <label className='block text-sm font-medium text-gray-700 mb-2'>
        {label}
      </label>

      {/* Selected Currency Display */}
      <button
        type='button'
        onClick={() => setIsOpen(!isOpen)}
        className='w-full flex items-center justify-between px-4 py-3 bg-white border-2 border-gray-200 rounded-lg hover:border-emerald-500 transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent'
      >
        {selectedCurrency ? (
          <div className='flex items-center space-x-3'>
            <span className='text-2xl'>{selectedCurrency.flag}</span>
            <div className='text-left'>
              <p className='font-semibold text-gray-900'>{selectedCurrency.code}</p>
              <p className='text-sm text-gray-600'>{selectedCurrency.name}</p>
            </div>
          </div>
        ) : (
          <span className='text-gray-500'>Select currency...</span>
        )}
        <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <Card className='absolute z-50 w-full mt-2 border-2 shadow-2xl max-h-[500px] overflow-hidden'>
          <div className='p-4 border-b bg-gray-50'>
            {/* Search */}
            <div className='relative mb-3'>
              <Search className='absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400' />
              <Input
                type='text'
                placeholder='Search currencies...'
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className='pl-10 pr-10'
                autoFocus
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className='absolute right-3 top-1/2 -translate-y-1/2'
                >
                  <X className='w-4 h-4 text-gray-400 hover:text-gray-600' />
                </button>
              )}
            </div>

            {/* Region Filter */}
            <div className='flex flex-wrap gap-2'>
              <Badge
                variant={selectedRegion === 'all' ? 'default' : 'outline'}
                className='cursor-pointer'
                onClick={() => setSelectedRegion('all')}
              >
                All
              </Badge>
              {REGIONS.map(region => (
                <Badge
                  key={region}
                  variant={selectedRegion === region ? 'default' : 'outline'}
                  className='cursor-pointer'
                  onClick={() => setSelectedRegion(region)}
                >
                  {region}
                </Badge>
              ))}
            </div>
          </div>

          {/* Popular Corridors */}
          {relevantCorridors.length > 0 && searchQuery === '' && selectedRegion === 'all' && (
            <div className='p-4 border-b bg-emerald-50'>
              <div className='flex items-center space-x-2 mb-2'>
                <TrendingUp className='w-4 h-4 text-emerald-600' />
                <p className='text-sm font-semibold text-emerald-900'>Popular Routes</p>
              </div>
              <div className='space-y-1'>
                {relevantCorridors.map(corridor => {
                  const targetCode = type === 'from' ? corridor.from : corridor.to;
                  const targetCurrency = CURRENCIES.find(c => c.code === targetCode);
                  
                  return (
                    <button
                      key={corridor.label}
                      onClick={() => handleSelect(targetCurrency)}
                      className='w-full flex items-center space-x-2 px-3 py-2 rounded-lg hover:bg-emerald-100 transition-colors text-left'
                    >
                      <span className='text-lg'>{targetCurrency.flag}</span>
                      <span className='text-sm font-medium text-gray-900'>{targetCurrency.code}</span>
                      <span className='text-xs text-gray-600'>- {corridor.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Currency List */}
          <div className='overflow-y-auto max-h-80'>
            {filteredCurrencies.length > 0 ? (
              <div className='p-2'>
                {filteredCurrencies.map(currency => (
                  <button
                    key={currency.code}
                    onClick={() => handleSelect(currency)}
                    className='w-full flex items-center justify-between px-3 py-3 rounded-lg hover:bg-gray-100 transition-colors group'
                  >
                    <div className='flex items-center space-x-3'>
                      <span className='text-2xl'>{currency.flag}</span>
                      <div className='text-left'>
                        <p className='font-semibold text-gray-900'>{currency.code}</p>
                        <p className='text-xs text-gray-600'>{currency.name}</p>
                      </div>
                    </div>
                    <div className='text-right'>
                      <p className='text-xs text-gray-500'>{currency.symbol}</p>
                      <p className='text-xs text-emerald-600'>1 USD = {currency.rate} {currency.code}</p>
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <div className='p-8 text-center text-gray-500'>
                <p>No currencies found</p>
                <p className='text-sm mt-1'>Try adjusting your search or filters</p>
              </div>
            )}
          </div>
        </Card>
      )}
    </div>
  );
};

export default CurrencySelector;
